import React, { useEffect, useState } from "react";
import { useDeal } from "../lib/store";
import { downAmt, loanAmt, exitReturns, fmt$, fmtPct, fmtX } from "../lib/model";
import { instantlyApi } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Clock } from "lucide-react";

export const TEMPLATE_KEYS = [["lp", "LP / Investor"], ["lender", "Lender"], ["broker", "Broker"], ["seller", "Off-market seller"], ["b2b", "B2B new client"]];

export const useDealFacts = () => {
  const { M, dealName, market } = useDeal();
  let irr = 0, moic = 0;
  try { ({ irr, moic } = exitReturns(M)); } catch { /* model not ready */ }
  const price = Number(M.askPrice) || 0, sde = Number(M.sde) || 0;
  return {
    deal: dealName || M.dealName || "", industry: M.industry || "", location: M.address || M.location || market?.location || "",
    revenue: M.ttmRevenue ? fmt$(M.ttmRevenue) : "", ebitda: sde ? fmt$(sde) : "", price: price ? fmt$(price) : "",
    equity: fmt$(downAmt(M) || 0), debt: fmt$(loanAmt(M) || 0), multiple: sde && price ? fmtX(price / sde) : "",
    irr: irr && !isNaN(irr) ? fmtPct(irr) : "", moic: moic ? fmtX(moic) : "", hold: String(M.holdYears || 5),
  };
};

// Template chips + editable steps + "Rewrite with AI". Controlled: value = { template, steps }.
export const SequenceEditor = ({ facts, value, onChange, compact, defaultTemplate = "" }) => {
  const [templates, setTemplates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [openStep, setOpenStep] = useState(0);
  const steps = value?.steps || [];

  useEffect(() => {
    instantlyApi.templates(facts).then((r) => {
      const list = r.templates || [];
      setTemplates(list);
      const t = defaultTemplate && !value?.template && list.find((x) => x.key === defaultTemplate);
      if (t) onChange({ template: t.key, steps: t.steps.map((s) => ({ ...s })) });
    }).catch(() => setTemplates([]));
  }, []); // eslint-disable-line

  const pick = (key) => {
    if (!key) return onChange({ template: "", steps: [] });
    const t = (templates || []).find((x) => x.key === key);
    if (t) { onChange({ template: key, steps: t.steps.map((s) => ({ ...s })) }); setOpenStep(0); }
  };
  const setStep = (i, patch) => onChange({ ...value, steps: steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const rewrite = async () => {
    if (!steps.length) return;
    setBusy(true);
    const tid = toast.loading("Claude is tailoring the sequence to this deal…");
    try {
      const t = (templates || []).find((x) => x.key === value.template);
      const r = await instantlyApi.rewriteTemplate({ steps, facts, audience: t?.audience || "", instruction });
      onChange({ ...value, steps: r.steps });
      toast.success("Sequence rewritten for this deal", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI rewrite failed", { id: tid });
    } finally { setBusy(false); }
  };

  return (
    <div className="seq" data-testid="sequence-editor">
      <div className="seq-chips">
        <button className={`chip ${!value?.template ? "on" : ""}`} onClick={() => pick("")} data-testid="seq-tpl-none">Blank</button>
        {TEMPLATE_KEYS.map(([k, label]) => (
          <button key={k} className={`chip ${value?.template === k ? "on" : ""}`} onClick={() => pick(k)} disabled={templates === null} data-testid={`seq-tpl-${k}`}>{label}</button>
        ))}
        {templates === null && <span className="cm-empty">Loading templates…</span>}
      </div>
      {!steps.length && <div className="cm-empty" data-testid="seq-empty">Pick a template — 3 pre-written emails using {"{{firstName}}"} / {"{{companyName}}"} and live facts from {facts.deal || "this deal"}. You can still edit every step.</div>}
      {steps.length > 0 && (
        <>
          <div className="seq-ai">
            <input className="inp" value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder='Optional instruction — e.g. "warmer tone" or "lead with the 8% pref"' data-testid="seq-ai-instruction" />
            <button className="btn btn-primary" onClick={rewrite} disabled={busy} data-testid="seq-rewrite"><Sparkles size={13} />{busy ? "Rewriting…" : "Rewrite with AI"}</button>
          </div>
          <div className="seq-steps">
            {steps.map((s, i) => (
              <div key={i} className={`seq-step ${openStep === i ? "open" : ""}`} data-testid={`seq-step-${i}`}>
                <button className="seq-step-h" onClick={() => setOpenStep(openStep === i ? -1 : i)} data-testid={`seq-step-toggle-${i}`}>
                  <span className="seq-n">{i + 1}</span>
                  <span className="seq-subj">{s.subject || "(no subject)"}</span>
                  <span className="seq-delay"><Clock size={11} />{i === 0 ? "Day 0" : `+${s.delay} ${s.delay_unit}`}</span>
                </button>
                {openStep === i && (
                  <div className="seq-step-b">
                    <div className="seq-row">
                      <input className="inp inp-text" value={s.subject} onChange={(e) => setStep(i, { subject: e.target.value })} placeholder="Subject" data-testid={`seq-subject-${i}`} />
                      {i > 0 && (
                        <label className="seq-wait">Wait
                          <input className="inp" type="number" min={0} value={s.delay} onChange={(e) => setStep(i, { delay: Math.max(0, parseInt(e.target.value || "0", 10)) })} data-testid={`seq-delay-${i}`} />
                          days
                        </label>
                      )}
                    </div>
                    <textarea className="inp" rows={compact ? 7 : 10} value={s.body} onChange={(e) => setStep(i, { body: e.target.value })} data-testid={`seq-body-${i}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Stand-alone modal: name a campaign, pick/edit a sequence, create it in Instantly (paused, ready for leads).
export const SequenceCampaignModal = ({ open, onClose, onCreated }) => {
  const facts = useDealFacts();
  const [name, setName] = useState("");
  const [seq, setSeq] = useState({ template: "", steps: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(facts.deal ? `${facts.deal} — outreach` : "XYZ Ventures outreach"); setSeq({ template: "", steps: [] }); } }, [open]); // eslint-disable-line
  if (!open) return null;
  const create = async () => {
    if (!name.trim()) return toast.error("Name the campaign");
    if (!seq.steps.length) return toast.error("Pick a sequence template first");
    setSaving(true);
    try {
      const c = await instantlyApi.createCampaign({ name: name.trim(), steps: seq.steps, template: seq.template });
      toast.success(`Campaign “${c.name}” created with ${seq.steps.length} emails — add leads from any CRM`);
      onCreated?.(c);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the campaign");
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" data-testid="sequence-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 760, width: "96%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title">New sequence campaign</div>
        <div className="modal-sub">Start an Instantly campaign from a pre-written LP, lender or broker sequence. It's created paused; load contacts from any CRM, then hit start in Instantly.</div>
        <div className="field"><label>Campaign name</label>
          <input className="inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="seq-camp-name" />
        </div>
        <SequenceEditor facts={facts} value={seq} onChange={setSeq} />
        <div className="modal-actions">
          <button className="btn" onClick={onClose} data-testid="seq-camp-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={create} disabled={saving || !seq.steps.length} data-testid="seq-camp-create">{saving ? "Creating…" : "Create campaign"}</button>
        </div>
      </div>
    </div>
  );
};
